import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  useHomeSections,
  useSessionStore,
  usePluginRegistry,
  useBundleModeStore,
  useAppSessionsStore,
  loadBundle,
  unloadBundle,
  getBundleUrl,
  LiquidBubbleNav,
  loadAppCatalog,
  MediaIcon,
  HOME_MEDIA,
  dispatchBackNavigation,
} from '@room/shared-ui';
import { getSessionToken } from '@room/frontend-sdk';
import { useBackgroundAuth } from '../hooks/useBackgroundAuth';
import { useHomeReady } from '../hooks/useHomeReady';
import { usePreloadTileIcons } from '../hooks/usePreloadTileIcons';
import { useTileDrag } from '../hooks/useTileDrag';
import { useLayoutSync } from '../hooks/useLayoutSync';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { LoginProgressPill } from '../components/LoginProgressPill';
import { WidgetBanner } from '../components/WidgetBanner';
import { AppSuggestions } from '../components/AppSuggestions';
import { SpotlightSearch } from '../components/SpotlightSearch';
import { AppSwitcher } from '../components/AppSwitcher';
import { SkeletonHome } from '../components/SkeletonHome';
import './HomePage.css';

/* ---- SVG Icons for bundle overlay navigation (SF Symbols style) ---- */
const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 17l-5-5 5-5" />
  </svg>
);

const ChevronLeftFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.7 18.7a1 1 0 0 1-1.4 0l-5.3-5.3a2 2 0 0 1 0-2.8l5.3-5.3a1 1 0 0 1 1.4 1.4L10.4 12l5.3 5.3a1 1 0 0 1 0 1.4z" />
  </svg>
);

const HomeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.2L12 3l9 7.2V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10.2z" />
    <path d="M9 21V14h6v7" />
  </svg>
);

const HomeFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.6 2.4a1 1 0 0 0-1.2 0l-9 7.2A1 1 0 0 0 2 10.2V20a2 2 0 0 0 2 2h5v-8h6v8h5a2 2 0 0 0 2-2V10.2a1 1 0 0 0-.4-.6l-9-7.2z" />
  </svg>
);

const ArrowClockwiseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 4 21 10 15 10" />
    <path d="M21 10l-3.5-3.5A8 8 0 1 0 20 15" />
  </svg>
);

const ArrowClockwiseFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 3a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-6a1 1 0 1 1 0-2h3.17l-2.46-2.46a7 7 0 1 0 2.46 6.96 1 1 0 1 1 1.96.4A9 9 0 1 1 18.12 5.88L21 3.83V3z" />
  </svg>
);

/** Check if an icon value is an image URL or data URI rather than an emoji */
function isImageIcon(icon: string): boolean {
  return (
    icon.startsWith('http://') ||
    icon.startsWith('https://') ||
    icon.startsWith('data:image/') ||
    icon.startsWith('/api/')
  );
}

/**
 * Render a tile icon.
 *
 * Priority:
 *  1. If the icon is an image URL / data URI → render <img> directly
 *  2. If a HOME_MEDIA entry exists for the tile → render <MediaIcon>
 *     (lazy-loads from R2, generates via SDXL Lightning on cache miss)
 *  3. Otherwise → render the raw emoji string
 */
function TileIcon({ icon, tileId }: { icon: string; tileId?: string }) {
  // Image URLs / data URIs bypass the media system
  if (isImageIcon(icon)) {
    return (
      <img
        src={icon}
        alt=""
        className="home-tile__icon-img"
        draggable={false}
        onError={(e) => {
          const target = e.currentTarget;
          const span = document.createElement('span');
          span.textContent = '📱';
          target.replaceWith(span);
        }}
      />
    );
  }

  // Always try MediaIcon for tiles — server generates from DB prompt if needed.
  // Falls back to emoji if no R2 image and no DB prompt exist.
  if (tileId) {
    return (
      <MediaIcon
        mediaId={tileId}
        fallback={icon}
        className="home-tile__media-icon"
      />
    );
  }

  return <span>{icon}</span>;
}

/** Media API base for R2-hosted assets */
const MEDIA_BASE = '/api/media';

/**
 * HomeWallpaper — full-screen background image loaded from the media system.
 *
 * Strategy:
 *   1. Try loading from R2: /api/media/home-wallpaper.png
 *   2. On 404: POST /api/media/generate to create via SDXL Lightning
 *   3. Fallback: keep the solid CSS background color
 */
function HomeWallpaper() {
  const [src, setSrc] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const triedGenerate = useRef(false);
  const entry = HOME_MEDIA['home-wallpaper'];

  useEffect(() => {
    if (!entry) return;
    setSrc(`${MEDIA_BASE}/${entry.id}.png`);
  }, [entry]);

  const handleLoad = useCallback(() => setLoaded(true), []);

  const handleError = useCallback(() => {
    if (triedGenerate.current || !entry) {
      setSrc(null);
      return;
    }
    triedGenerate.current = true;

    fetch(`${MEDIA_BASE}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: entry.id,
        prompt: entry.prompt,
        resolution: entry.resolution,
        negativePrompt: entry.negativePrompt,
        model: entry.model,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Generate failed: ${res.status}`);
        setSrc(`${MEDIA_BASE}/${entry.id}.png?t=${Date.now()}`);
      })
      .catch(() => setSrc(null));
  }, [entry]);

  if (!src) return null;

  return (
    <div className={`home-wallpaper ${loaded ? 'home-wallpaper--loaded' : ''}`}>
      <img
        className="home-wallpaper__img"
        src={src}
        alt=""
        draggable={false}
        onLoad={handleLoad}
        onError={handleError}
      />
      <div className="home-wallpaper__overlay" />
    </div>
  );
}

export function HomePage() {
  // Auto-register enabled plugins as tiles
  usePluginRegistry();

  // Background Pi authentication - auto-login without blocking UI
  const authProgress = useBackgroundAuth();
  const authDone = authProgress.phase === 'done';

  // Persist tile layout to server (syncs with /api/settings).
  // Starts immediately if a cached token exists (optimistic parallel fetch),
  // retries after auth if the optimistic fetch fails.
  const layoutSynced = useLayoutSync(authDone);

  // Sync app catalog (installed apps) from server.
  // Optimistic: if a cached token exists, start immediately in parallel
  // with auth validation. Retry after auth if the optimistic fetch failed.
  const [catalogSettled, setCatalogSettled] = useState(false);
  const catalogFetchedRef = useRef(false);
  const catalogSucceededRef = useRef(false);
  const hasCachedToken = useRef(!!getSessionToken()).current;

  // Optimistic catalog fetch — runs on mount if cached token exists
  useEffect(() => {
    if (!hasCachedToken || catalogFetchedRef.current) return;
    catalogFetchedRef.current = true;

    loadAppCatalog()
      .then(() => { catalogSucceededRef.current = true; })
      .catch(() => { /* will retry after auth */ })
      .finally(() => setCatalogSettled(true));
  }, [hasCachedToken]);

  // Post-auth catalog fetch — retry if optimistic failed, or first fetch
  // if no cached token existed
  useEffect(() => {
    if (!authDone) return;
    if (!catalogFetchedRef.current) {
      // No cached token — this is the first fetch
      catalogFetchedRef.current = true;
      loadAppCatalog()
        .catch(() => { /* silently ignore */ })
        .finally(() => setCatalogSettled(true));
      return;
    }
    if (catalogSucceededRef.current) return;

    // Optimistic fetch failed, auth now succeeded — retry with fresh token
    setCatalogSettled(false);
    loadAppCatalog()
      .then(() => { catalogSucceededRef.current = true; })
      .catch(() => { /* silently ignore */ })
      .finally(() => setCatalogSettled(true));
  }, [authDone]);

  // Preload tile icons into browser cache once catalog and layout are settled.
  // This prevents the flash where tiles appear without their icons.
  const dataReady = catalogSettled && layoutSynced;
  const iconsReady = usePreloadTileIcons(dataReady);

  // Skeleton loading: wait until ALL async steps complete before revealing.
  // This prevents the flash where tiles appear then shift/disappear
  // because catalog, layout sync, or icon loading hasn't finished yet.
  const isHomeReady = useHomeReady({
    authPhase: authProgress.phase,
    catalogSettled,
    layoutSynced,
    iconsReady,
  });

  // Drag & drop system
  const { onTilePointerDown, exitEditMode, removeTileFromDock, dragState } = useTileDrag();

  // Spotlight search & app switcher overlays
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const pushSession = useAppSessionsStore((s) => s.pushSession);

  const swipeHandlers = useSwipeGesture({
    onSwipeDown: useCallback(() => setShowSpotlight(true), []),
    onSwipeUp: useCallback(() => setShowAppSwitcher(true), []),
    disabled: dragState.isEditMode,
  });

  const sections = useHomeSections();
  const userRole = useSessionStore((state) => state.user?.role);
  const {
    isBundleMode,
    activeBundleApp,
    setActiveBundleApp,
    setBundleMode,
  } = useBundleModeStore();
  const [bundleError, setBundleError] = useState<string | null>(null);
  const deepLinkHandled = useRef(false);

  // Detect deep link app from URL (e.g. /chat/group/123 → 'chat')
  const deepLinkApp = useMemo(() => {
    const pathname = window.location.pathname;
    const appName = pathname.split('/').filter(Boolean)[0];
    if (appName && appName !== 'home') return appName;
    return null;
  }, []);

  // Auto-restore bundle from URL path when in bundle mode
  useEffect(() => {
    if (deepLinkHandled.current) return;
    if (activeBundleApp) return;

    if (deepLinkApp) {
      deepLinkHandled.current = true;

      if (!isBundleMode) {
        setBundleMode(true);
      }

      // Set active app FIRST so React renders #bundle-container before mount
      setActiveBundleApp(deepLinkApp);

      const bundleUrl = getBundleUrl(deepLinkApp);
      loadBundle(deepLinkApp, bundleUrl)
        .then((bundle) => {
          bundle.mount('bundle-container');
          console.log(`[Home] Deep link: loaded ${deepLinkApp} bundle from URL`);
        })
        .catch((error) => {
          console.error('[Home] Failed to load bundle from deep link:', error);
          (window as any).__ROOM_BUNDLE_MODE__ = false;
          setActiveBundleApp(null);
          window.history.replaceState({}, '', '/');
        });
      return;
    }

    if (!isBundleMode) return;

    const pathname = window.location.pathname;
    const appName = pathname.split('/').filter(Boolean)[0];

    if (appName && appName !== 'home') {
      // Set active app FIRST so React renders #bundle-container before mount
      setActiveBundleApp(appName);

      const bundleUrl = getBundleUrl(appName);
      loadBundle(appName, bundleUrl)
        .then((bundle) => {
          bundle.mount('bundle-container');
        })
        .catch((error) => {
          console.error('Failed to restore bundle from URL:', error);
          (window as any).__ROOM_BUNDLE_MODE__ = false;
          setActiveBundleApp(null);
          window.history.replaceState({}, '', '/');
        });
    }
  }, [isBundleMode, activeBundleApp, setActiveBundleApp, deepLinkApp, setBundleMode]);

  useEffect(() => {
    let isMounted = true;
    fetch('/api/config')
      .then((response) => response.json())
      .then((payload) => {
        if (!isMounted) return;
        const bundleModeDefault = payload?.data?.['ui.bundle.mode'];
        if (bundleModeDefault === 'bundle' || bundleModeDefault === 'browser') {
          // Don't override bundle mode while an app is actively loaded —
          // switching to browser mode mid-session would disrupt the overlay.
          if (!useBundleModeStore.getState().activeBundleApp) {
            setBundleMode(bundleModeDefault === 'bundle');
          }
        }
      })
      .catch((error) => {
        console.warn('[Home] Failed to load public config, using defaults.', error);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const resolvedSections = useMemo(() => {
    return sections.map((section) => ({
      ...section,
      items: section.items.filter((tile) => (tile.visible ? tile.visible() : true)),
    }));
  }, [sections, userRole]);

  // Open an app by route - loads as bundle overlay or navigates.
  // Also tracks the app in the multitasking session store.
  const openApp = async (route: string) => {
    // Don't open apps while in edit mode
    if (dragState.isEditMode) return;

    const pathname = route.split('?')[0];
    const appName = pathname.split('/').filter(Boolean)[0];
    if (!appName) return;

    // Find tile metadata for session tracking
    const tile = resolvedSections
      .flatMap((s) => s.items)
      .find((t) => t.route.startsWith(`/${appName}`));

    if (tile) {
      pushSession({
        appId: tile.id,
        label: tile.label,
        icon: tile.icon,
        route,
      });
    } else {
      pushSession({ appId: appName, label: appName, icon: '📱', route });
    }

    if (!isBundleMode) {
      window.location.href = route;
      return;
    }

    setBundleError(null);
    try {
      if (activeBundleApp && activeBundleApp !== appName) {
        unloadBundle(activeBundleApp);
      }

      setActiveBundleApp(appName);
      window.history.pushState({}, '', route);

      const bundleUrl = getBundleUrl(appName);
      const bundle = await loadBundle(appName, bundleUrl);
      bundle.mount('bundle-container');
    } catch (error) {
      console.error('Failed to load bundle:', error);
      (window as any).__ROOM_BUNDLE_MODE__ = false;
      setBundleError(
        error instanceof Error ? error.message : 'Failed to load app bundle'
      );
      setActiveBundleApp(null);
      window.history.replaceState({}, '', '/');
    }
  };

  // Handle tile click - either navigate or load bundle
  const handleTileClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    _tileId: string,
    route: string
  ) => {
    // Prevent navigation in edit mode
    if (dragState.isEditMode) {
      e.preventDefault();
      return;
    }
    if (!isBundleMode) return;
    e.preventDefault();
    openApp(route);
  };

  const handleCloseBundle = () => {
    if (activeBundleApp) {
      unloadBundle(activeBundleApp);
      setActiveBundleApp(null);
      setBundleError(null);
      exitEditMode();
      window.history.pushState({}, '', '/');
    }
  };

  const mainSection = resolvedSections.find((section) => section.id === 'main');
  const dockSection = resolvedSections.find((section) => section.id === 'dock');
  const tilesPerPage = 12;
  const mainPages = useMemo(() => {
    const items = mainSection?.items ?? [];
    const pages: typeof items[] = [];
    for (let index = 0; index < items.length; index += tilesPerPage) {
      pages.push(items.slice(index, index + tilesPerPage));
    }
    return pages.length > 0 ? pages : [[]];
  }, [mainSection?.items]);

  // The dragged tile data for the floating clone
  const draggedTile = useMemo(() => {
    if (!dragState.draggingTileId) return null;
    for (const section of resolvedSections) {
      const tile = section.items.find((t) => t.id === dragState.draggingTileId);
      if (tile) return tile;
    }
    return null;
  }, [dragState.draggingTileId, resolvedSections]);

  return (
    <>
      {/* Skeleton: visible while loading, fades out when ready */}
      <div
        className={`home-skeleton-wrapper ${isHomeReady ? 'home-skeleton-wrapper--hidden' : ''}`}
        aria-hidden={isHomeReady}
      >
        <SkeletonHome />
      </div>

      {/* Login progress pill — rendered outside .home-screen so
          the `> *:not(.home-wallpaper) { position: relative }` rule
          doesn't override its position: fixed and cause layout shift */}
      <LoginProgressPill {...authProgress} />

      {/* Real home: hidden initially, fades in when ready */}
      <div
        className={`home-screen ${isHomeReady ? 'home-screen--ready' : 'home-screen--loading'} ${dragState.isEditMode ? 'home-screen--edit-mode' : ''}`}
        onClick={(e) => {
          // Click on background exits edit mode
          if (dragState.isEditMode && (e.target as HTMLElement).classList.contains('home-screen')) {
            exitEditMode();
          }
        }}
        {...swipeHandlers}
      >
        <HomeWallpaper />

        {/* Swipe gesture hint indicators */}
        <div className="home-screen__swipe-hint home-screen__swipe-hint--top" aria-hidden="true" />

        {/* Widget Banner - multi-functional first row */}
        <WidgetBanner />

        {/* App Suggestions - discover uninstalled apps */}
        <AppSuggestions onOpenApp={openApp} />

        <div className="home-screen__pages" aria-label="Home pages">
          {mainPages.map((page, pageIndex) => (
            <div key={`home-page-${pageIndex}`} className="home-screen__page">
              <div className="home-screen__grid" data-droppable="main">
                {page.map((tile) => (
                  <a
                    key={tile.id}
                    href={tile.route}
                    className={`home-tile ${dragState.isEditMode ? 'home-tile--wiggle' : ''} ${dragState.draggingTileId === tile.id ? 'home-tile--dragging' : ''}`}
                    data-tile-id={tile.id}
                    onClick={(e) => handleTileClick(e, tile.id, tile.route)}
                    onPointerDown={(e) => onTilePointerDown(tile.id, 'main', e)}
                  >
                    <div className="home-tile__icon">
                      <TileIcon icon={tile.icon} tileId={tile.id} />
                      {tile.badge && tile.badge() ? (
                        <span className="home-tile__badge">{tile.badge()}</span>
                      ) : null}
                    </div>
                    <div className="home-tile__label">{tile.label}</div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        {mainPages.length > 1 ? (
          <div className="home-screen__page-indicator" aria-hidden="true">
            {mainPages.map((_, index) => (
              <span key={`home-dot-${index}`} className="home-screen__dot" />
            ))}
          </div>
        ) : null}

        <div
          className={`home-screen__dock ${dragState.isEditMode ? 'home-screen__dock--edit-mode' : ''} ${dragState.hoverSection === 'dock' ? 'home-screen__dock--drop-active' : ''}`}
          data-droppable="dock"
        >
          {dockSection?.items.map((tile) => (
            <a
              key={tile.id}
              href={tile.route}
              className={`home-tile home-tile--dock ${dragState.isEditMode ? 'home-tile--wiggle' : ''} ${dragState.draggingTileId === tile.id ? 'home-tile--dragging' : ''}`}
              data-tile-id={tile.id}
              onClick={(e) => handleTileClick(e, tile.id, tile.route)}
              onPointerDown={(e) => onTilePointerDown(tile.id, 'dock', e)}
            >
              <div className="home-tile__icon">
                <TileIcon icon={tile.icon} tileId={tile.id} />
                {tile.badge && tile.badge() ? (
                  <span className="home-tile__badge">{tile.badge()}</span>
                ) : null}
                {dragState.isEditMode && (
                  <button
                    className="home-tile__remove-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeTileFromDock(tile.id);
                    }}
                    aria-label={`Remove ${tile.label} from dock`}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="7" fill="rgba(60,60,60,0.9)" />
                      <path d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="home-tile__label">{tile.label}</div>
            </a>
          ))}
        </div>

        {/* Edit mode "Done" button */}
        {dragState.isEditMode && (
          <button className="home-screen__done-btn" onClick={exitEditMode}>
            Done
          </button>
        )}

        {/* Floating drag clone */}
        {draggedTile && dragState.dragPosition && (
          <div
            className="home-tile__drag-clone"
            style={{
              left: dragState.dragPosition.x,
              top: dragState.dragPosition.y,
            }}
          >
            <div className="home-tile__icon">
              <TileIcon icon={draggedTile.icon} tileId={draggedTile.id} />
            </div>
            <div className="home-tile__label">{draggedTile.label}</div>
          </div>
        )}

      </div>

      {/* Spotlight Search — swipe down from home */}
      <SpotlightSearch
        isOpen={showSpotlight}
        onClose={() => setShowSpotlight(false)}
        onOpenApp={openApp}
      />

      {/* App Switcher — swipe up from home */}
      <AppSwitcher
        isOpen={showAppSwitcher}
        onClose={() => setShowAppSwitcher(false)}
        onOpenApp={openApp}
      />

      {/* Bundle Container Overlay — rendered OUTSIDE .home-screen so
          the `.home-screen > *:not(.home-wallpaper)` rule (position: relative)
          cannot override position: fixed on the overlay. */}
      {activeBundleApp && (
        <div className="bundle-overlay bundle-overlay--with-navigation">
          <div id="bundle-container" className="bundle-overlay__content"></div>
          <LiquidBubbleNav
            items={[
              { id: 'back', label: 'Back', icon: <ChevronLeftIcon />, activeIcon: <ChevronLeftFilledIcon />, onClick: () => dispatchBackNavigation(activeBundleApp) },
              { id: 'home', label: 'Home', icon: <HomeIcon />, activeIcon: <HomeFilledIcon />, onClick: handleCloseBundle },
              {
                id: 'refresh',
                label: 'Refresh',
                icon: <ArrowClockwiseIcon />,
                activeIcon: <ArrowClockwiseFilledIcon />,
                onClick: async () => {
                  if (!activeBundleApp) return;
                  try {
                    unloadBundle(activeBundleApp);
                    const bundleUrl = getBundleUrl(activeBundleApp);
                    const bundle = await loadBundle(activeBundleApp, bundleUrl);
                    bundle.mount('bundle-container');
                  } catch (error) {
                    console.error('Failed to refresh bundle:', error);
                    setBundleError(
                      error instanceof Error ? error.message : 'Failed to refresh app'
                    );
                  }
                },
              },
            ]}
            activeId="home"
          />
          {bundleError && (
            <div className="bundle-overlay__error">
              <p>Error loading app: {bundleError}</p>
              <button onClick={handleCloseBundle}>Close</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
